// Section 14 emigration pressure (stream E2). Each adult's `emigrationPressure` (the D-15 terms
// are contributed by contributors/events.ts: 0.4 x (100 - happiness), 0.4 x (100 - trust),
// 10 x safety deficit, 5 per hungry season; Restless 1.25x; norms may scale it). Over the
// threshold for 3 consecutive seasons, the person plans to leave with their household at the
// start of the next Spring or Summer, for the dominant term's reason. Guests are not counted
// (they are decided on at the gate).
import { departureTurn } from '../../core/content-helpers';
import { peopleSorted } from '../../core/queries';
import type { EmigrationReason, Factor, PhaseContext, Target } from '../../core/types';
import { applyDelta } from '../shared/deltas';
import { num, valueOf } from '../shared/value';

const REASONS: Record<string, EmigrationReason> = {
  'migration:unhappy': 'unhappy',
  'migration:distrust': 'distrust',
  'migration:unsafe': 'unsafe',
  'migration:hunger': 'hunger',
  'trait:restless': 'restless',
};

/** The stated reason: the term that added most to the pressure. */
export function dominantReason(factors: readonly Factor[]): EmigrationReason {
  let best: EmigrationReason = 'unhappy';
  let most = Number.NEGATIVE_INFINITY;
  for (const f of factors) {
    const reason = REASONS[f.source];
    if (reason !== undefined && f.delta > most) {
      most = f.delta;
      best = reason;
    }
  }
  return best;
}

export function updateEmigration(ctx: PhaseContext): void {
  const m = ctx.cfg.migration;
  const people = ctx.draft.people;
  // Read the committed person (this phase changes no streak before here) and draft them only
  // to write (Section 2 budget); people who left earlier in the phase are skipped.
  for (const p of peopleSorted(ctx.state)) {
    const id = p.id;
    if (!(id in people)) continue;
    const eligible =
      p.ageSeasons >= ctx.cfg.people.adultAtSeasons &&
      p.status.guest === null &&
      p.plannedDeparture === null;
    const target: Target = { kind: 'emigrationPressure', personId: id };
    const over = eligible && num(ctx, target) > m.emigrationThreshold;
    const streak = over ? p.streaks.emigration + 1 : 0;
    if (p.streaks.emigration !== streak) {
      const draft = people[id];
      if (draft !== undefined) draft.streaks.emigration = streak;
    }
    if (!over || streak < m.emigrationSeasons) continue;
    applyDelta(ctx, {
      kind: 'planDeparture',
      personId: id,
      reason: dominantReason(valueOf(ctx, target).factors),
      atTurn: departureTurn(ctx.live, ctx.season.turn + 1),
    });
  }
}
